import type { ReactNode } from 'react';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { classNames } from '../../utils/classNames';

interface PickerSectionProps {
  label: string;
  theme: 'dark' | 'light';
  children: ReactNode;
}

export function PickerSection({ label, theme, children }: PickerSectionProps) {
  const isDark = theme === 'dark';

  return (
    <fieldset
      className={classNames(
        'p-6',
        isDark ? 'bg-neutral-600 print:bg-neutral-100' : 'bg-neutral-100',
      )}
    >
      <legend className="sr-only">{label}</legend>
      <span
        aria-hidden="true"
        className={classNames(
          'tra-text-web-sub-2 block pb-6',
          isDark ? 'text-neutral-100 print:text-neutral-600' : 'text-neutral-600',
        )}
      >
        {label}
      </span>
      {children}
    </fieldset>
  );
}

interface PickerOption {
  key: string;
  label: ReactNode;
  isSelected: boolean;
  onSelect: () => void;
}

interface PickerButtonGroupProps {
  options: Array<PickerOption>;
  direction?: 'row' | 'column';
  theme: 'dark' | 'light';
}

export function PickerButtonGroup({ options, direction = 'row', theme }: PickerButtonGroupProps) {
  const isDark = theme === 'dark';

  return (
    <div
      className={direction === 'row' ? 'grid grid-flow-col auto-cols-fr' : 'flex flex-col'}
      role="group"
    >
      {options.map((option) => (
        <Button
          key={option.key}
          type="button"
          aria-pressed={option.isSelected}
          variant={ButtonVariant.bare}
          size="medium"
          onClick={option.onSelect}
          className={classNames(
            '!rounded-none w-full border transition-colors',
            direction === 'column' && 'flex items-center justify-between text-left',
            isDark ? 'text-white print:text-neutral-900' : 'text-neutral-900',
            direction === 'row' ? '!px-2' : '!px-4 sm:!px-8',
            option.isSelected && 'border-brand-300',
            option.isSelected
              ? isDark
                ? 'bg-neutral-500 print:bg-neutral-200'
                : 'bg-warm-100'
              : isDark
                ? 'border-neutral-300 print:border-transparent-light-200'
                : 'border-transparent-light-200',
          )}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
